import { handleActions } from 'redux-actions';
import {reset, SubmissionError, getFormValues, change } from 'redux-form';
import moment from 'moment';
import qs from 'qs';

import cloneDeep from 'lodash/cloneDeep';
import _get from 'lodash/get';
import _forEach from 'lodash/forEach';
import _find from 'lodash/find';

import { LOCATION_CHANGE, replace } from 'react-router-redux';

import http from '../../shared/http';
import intl from '../../shared/intl';
import checkPermissions from '../../shared/check-permissions';
import csvExport from '../../shared/csvExport';
import endpoints from '../../../config/endpoints';

import createFilters from '../../shared/fixed-filters';

import {default as createCalendar, getDateRangeFromCalendar} from '../../shared/calendar';

import { createSelectOptionsFromSysEnums } from '../../shared/form-elements/select';
import {regions} from '../../../../../../enums/regions';

const calendar = createCalendar({
	namespace: 'payoutRequests',
	stateNodeName: 'calendar',
	defaultValue: {
		start: moment().subtract(1, 'month').format('YYYY-MM-DD'),
		end: moment().format('YYYY-MM-DD')
	}
});

const filters = createFilters({
	namespace: 'topAffiliatesList',
	stateNodeName: 'filters',
	queryBuildFunc: () => '',
	listOfFilters: [
		{
			name: 'admins',
			title: 'Manager name',
			placeholder: 'Manager name',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.ADMINS,
				params: 'only_managers=true&limit=100&is_show_name=true'
			}
		},
		{
			name: 'affiliates',
			title: 'Affiliates',
			placeholder: 'Add affiliates',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.AFFILIATE_IDS
			}
		},
		{
			name: 'offers',
			title: 'Offers',
			placeholder: 'Add offers',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.OFFERS
			}
		},
		{
			name: 'country_ids',
			title: 'Countries',
			placeholder: 'Add country',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.LOCATIONS_WITH_TRAFFIC,
				params: 'limit=100'
			}
		},
		{
			name: 'niches',
			title: 'Niches',
			placeholder: 'Select niches',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.OFFERS_NICHES
			}
		},
		{
			name: 'regions',
			title: 'Regions',
			placeholder: 'Add regions',
			value: [],
			multi: true,
			searchable: false,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'provided',
				source: regions.map(reg => ({
					value: reg,
					label: reg
				}))
			}
		}
	]
});

// action types
const FETCH_DATA_SUCCESS = 'payoutRequests/fetch-data-success';
const FETCH_DATA_ERROR = 'payoutRequests/fetch-data-error';
const SHOW_PROGRESS_BAR = 'payoutRequests/SHOW_PROGRESS_BAR';

const FETCH_MATRIX_SUCCESS = 'payoutRequests/FETCH_MATRIX_SUCCESS';
const FETCH_MATRIX_ERROR = 'payoutRequests/FETCH_MATRIX_ERROR';

const TOGGLE_REQUEST_FORM = 'payoutRequests/TOGGLE_REQUEST_FORM';
const TOGGLE_EDIT_FORM = 'payoutRequests/TOGGLE_EDIT_FORM';

const FETCH_SUGGEST_SUCCESS = 'payoutRequests/fetch-suggest-success';
const FETCH_SUGGEST_ERROR = 'payoutRequests/fetch-suggest-error';

const REQUEST_SUCCESS = 'payoutRequests/REQUEST_SUCCESS';
const REQUEST_ERROR = 'payoutRequests/REQUEST_ERROR';

const TOGGLE_PROGRESS_BAR = 'payoutRequests/toggle-progress-bar';

const TOGGLE_SORT = 'payoutRequests/TOGGLE_SORT';

const APPLY = 'payoutRequests/apply';

const SUBMIT_SUCCESS = 'payoutRequests/submit-success';
const APPEND_MAX_LEADS_TO_REQUEST = 'payoutRequests/APPEND_MAX_LEADS_TO_REQUEST';
const SUBMIT_ERROR = 'payoutRequests/submit-error';

const FETCH_CURRENT_PAYOYT_SUCCESS = 'payoutRequests/FETCH_CURRENT_PAYOYT_SUCCESS';
const FETCH_MAX_DAILY_LEADS = 'payoutRequests/FETCH_MAX_DAILY_LEADS';

// permissions
export const allowedPermissions = {
	payout_requests_personal_view: 'payout_requests_personal_view',
	payout_requests_view_all: 'payout_requests_view_all',
	payout_requests_review: 'payout_requests_review',
	payout_requests_view_matrix_and_history: 'payout_requests_view_matrix_and_history',
	payout_requests_manager_role: 'payout_requests_manager_role',
	payout_requests_confirm: 'payout_requests_confirm'
};

const table = {
	columns: {
		id: { index: 'id', title: 'ID', canBeSorted: true },
		manager: { index: 'manager', className: '', title: 'Manager' },
		aff_geo_country: { index: 'aff_geo_country', className: 'aff-col', title: 'Aff & Off & Geo' },
		maxDailyLeads: { index: 'maxDailyLeads', className: 'short-col', title: 'Max daily leads' },
		has_conversions: {
			index: 'has_conversions',
			className: 'short-col have-traf-col',
			title: '<div><p>Have traffic<p><p>for offer?</p></div>'
		},
		sid_campaign: {
			index: 'sid_campaign',
			className: '',
			title: 'Sid & Campaign ID'
		},
		// analog_payouts: {
		// 	index: 'analog_payouts',
		// 	className: '',
		// 	title: 'Analog payouts'
		// },
		// analog_traffic_exists: {
		// 	index: 'analog_traffic_exists',
		// 	className: '',
		// 	title: 'Analog traffic'
		// },
		current_payout: {
			index: 'current_payout', className: 'short-col', title: '<div><p>Current<p><p>payout</p></div>'
		},
		amount: {
			index: 'amount', className: 'short-col', title: '<div><p>Requested<p><p>payout</p></div>'
		},
		garant_percent: {
			index: 'garant_percent',
			className: 'short-col',
			title: '<div><p>Garant<p><p>approve, %</p></div>'
		},
		traffic_source: { index: 'traffic_source', className: '', title: 'Traffic source' },
		planned_volume: {
			index: 'planned_volume', className: 'short-col',
			title: '<div><p>Planned<p><p>volume</p></div>'
		},
		recommended_payout: {
			index: 'recommended_payout', className: '', title: '<div><p>Recommended<p><p>payout</p></div>',
			visibleWithPermission: 'payout_requests_review',
			isHidden: process.env.CPA_ENV === 'rocket'
		},
		payout_decision: { index: 'payout_decision', className: '', title: 'Payout decision' },
		granted_volume: { index: 'granted_volume', className: '', title: 'Granted volume' },
		comment: { index: 'comment', className: '', title: 'Comment' },
		analytic_comment: { index: 'analytic_comment', className: '', title: 'Analytic comment' },
		status: { index: 'status', className: '', title: 'Status' }
	},
	sortByColumn: {
		index: null,
		direction: null
	}
};
// initial state
const initialState = {
	data: null,
	systemBalances: {},
	calendar: calendar.initialState,
	table,
	progressBar: false,
	showProgressBar: false,
	filters: filters.initialState,
	error: false,
	maxDailyLeads: 0,
	isRequestFormVisible: false,
	isRequestEditFormVisible: false,
	sortedPayoutSystemsList: [],
	payoutMatrix: []
};

// actions
export const actions = Object.assign(
	{
		fetchData: props => (dispatch, getState) => {
			const { calendar, filters: { selectedFilters } } = getState().payoutRequests;

			const query = {
				filters: {}
			};

			if (document.isLoading) {
				return;
			}

			document.isLoading = true;

			if (selectedFilters) {
				selectedFilters.forEach(filter => {
					const values = filter.value.map(f => f.value);
					if (values.length) {
						query.filters[filter.name] = values;
					}
				});
			}

			query.date_range = getDateRangeFromCalendar(calendar);
			query.sort = selectors.getSortByColumn(getState());

			return http({
				url: `${endpoints.PAYOUT_REQUESTS.BASE}?${qs.stringify(query)}`,
				method: 'get',
				actions: [FETCH_DATA_SUCCESS, FETCH_DATA_ERROR]
			})(dispatch)
				.then(async action => {
					document.isLoading = false;
					if (action.type === FETCH_DATA_SUCCESS) {
						// frontend/admin-dashboard/src/modules/payout-requests/list-request/index.js
						// /max-daily-valid-leads
						for (const item of action.payload.data) {
							await http({
								url: `${endpoints.PAYOUT_REQUESTS.BASE}/max-daily-valid-leads?${qs.stringify(item)}`,
								method: 'get',
								addToResponse: item.id,
								actions: [APPEND_MAX_LEADS_TO_REQUEST, FETCH_MATRIX_ERROR]
							})(dispatch);
						}
					}
					if (process.env.CPA_ENV === 'everad' && selectors.getCan(getState())('payout_requests_review')) {
						return http({
							url: `${endpoints.PAYOUT_REQUESTS.BASE}/matrix?${qs.stringify({is_simple_result: true})}`,
							method: 'get',
							actions: [FETCH_MATRIX_SUCCESS, FETCH_MATRIX_ERROR]
						})(dispatch);
					}
				});
		},
		download: () => (dispatch, getState) => {
			const { rows } = selectors.getTable(getState());
			const headers = Object.keys(rows[0]);

			const columns = rows.map(entity => headers.map(h => entity[h] || '-'));

			const rowsForExport = [].concat([headers], columns);

			csvExport('Payout_requests', rowsForExport);
		},

		apply: () => (dispatch, getState) => {
			dispatch(actions.fetchData());
		},

		getCurrentPayout: () => (dispatch, getState) => {
			const formData = getFormValues('createPayoutRequestForm')(getState());

			const sid = _get(formData, 'sid.0.value', '');
			const sid_type = _get(formData, 'sid_type.0.value', '');
			const campaign_id = _get(formData, 'campaign_id.0.value', '');

			const query = {
				currency: 'usd',
				affiliateId: _get(formData, 'affiliate_id.0.value', null),
				locationId: _get(formData, 'country_id.0.value', null),
				id: _get(formData, 'offer_id.0.value', null)
			};

			if (campaign_id) {
				query.campaign_id = campaign_id;
			}

			if (sid_type && sid) {
				query.sids = {[sid_type]: sid };
			}

			return http({
				url: `${endpoints.PAYOUT_REQUESTS.CURRENT_PAYOUT}?${qs.stringify(query)}`,
				method: 'get',
				actions: [FETCH_CURRENT_PAYOYT_SUCCESS, FETCH_DATA_ERROR]
			})(dispatch)
				.then(action => {
					if (action.type === FETCH_CURRENT_PAYOYT_SUCCESS) {
						dispatch(actions.getMaxDailyLeadsDuringWeek(query));
					}
				});
		},
		getMaxDailyLeadsDuringWeek: (query) => dispatch => {
			return http({
				url: `${endpoints.PAYOUT_REQUESTS.MAX_WEEKLY_CONVERSIONS}?${qs.stringify(query)}`,
				method: 'get',
				actions: [FETCH_MAX_DAILY_LEADS, FETCH_DATA_ERROR]
			})(dispatch);
		},

		toggleRequestForm: () => ({ type: TOGGLE_REQUEST_FORM }),

		changeEditFormFieldToDefault: () => (dispatch) => {
			for (const field of ['campaign_id', 'sid_type', 'sid'])
				dispatch(change('editPayoutRequestForm', field, null));
		},

		toggleEditForm: (id) => ({ type: TOGGLE_EDIT_FORM, payload: id }),

		clear: () => (dispatch, getState) => {
			dispatch(replace('/payouts/'));
		},
		submitPayoutRequest: (formData) => (dispatch, getState) => {

			let submitData = {
				...formData,
				currency: 'usd',
				granted_volume: formData.planned_volume,
				affiliate_id: _get(formData, 'affiliate_id.0.value', null),
				campaign_id: _get(formData, 'campaign_id.0.value', null),
				country_id: _get(formData, 'country_id.0.value', null),
				traffic_source: _get(formData, 'traffic_source.0.value', null),
				offer_id: _get(formData, 'offer_id.0.value', null),
				sid: _get(formData, 'sid.0.value', null),
				sid_type: _get(formData, 'sid_type.0.value', null)
			};

			if (_get(formData, 'country_id.1.value')) {
				submitData = formData.country_id
					.map(item => item.value)
					.filter(Boolean)
					.map(country_id => ({...submitData, country_id}));
			}

			return http({
				url: endpoints.PAYOUT_REQUESTS.BASE,
				method: 'post',
				data: submitData,
				actions: [SUBMIT_SUCCESS, SUBMIT_ERROR]
			})(dispatch)
				.then(action => {
					if (action.type === SUBMIT_SUCCESS) {
						dispatch(actions.toggleRequestForm());
						dispatch(reset('createPayoutRequestForm'));
						dispatch(actions.fetchData());
					} else {
						const validationsErrors = {};
						let responseErrors = action.payload.response.data;

						if (responseErrors.errors && responseErrors.errors.length) {
							responseErrors = responseErrors.errors;
						}

						_forEach(responseErrors, value => {
							const key = value.dataPath.slice(1);
							validationsErrors[key] = value.message;
						});

						throw new SubmissionError(validationsErrors);
					}
				});
		},
		submitPayoutRequestEdit: (formData) => (dispatch, getState) => {

			let submitData = {
				...formData,
				country_id: _get(formData, 'country_id.0.value', null),
				campaign_id: _get(formData, 'campaign_id.0.value', null),
				sid_type: _get(formData, 'sid_type.0.value', null),
				sid: _get(formData, 'sid.0.value', null)
			};
			const id = selectors.getEditingId(getState());

			return http({
				url: endpoints.PAYOUT_REQUESTS.SINGLE.replace('{id}', id),
				method: 'put',
				data: submitData,
				actions: [SUBMIT_SUCCESS, SUBMIT_ERROR]
			})(dispatch)
				.then(action => {
					if (action.type === SUBMIT_SUCCESS) {
						dispatch(actions.toggleEditForm());
						dispatch(reset('editPayoutRequestForm'));
						dispatch(actions.fetchData());
					} else {
						const validationsErrors = {};
						const responseErrors = action.payload.response.data;

						_forEach(responseErrors, value => {
							const key = value.dataPath.slice(1);
							validationsErrors[key] = value.message;
						});

						throw new SubmissionError(validationsErrors);
					}
				});
		},
		updatePayoutRequest: (id, formData) => (dispatch, getState) => {

			return http({
				url: `${endpoints.PAYOUT_REQUESTS.BASE}/${id}`,
				method: 'put',
				data: formData,
				actions: [SUBMIT_SUCCESS, SUBMIT_ERROR]
			})(dispatch)
				.then(action => {
					if (action.type === SUBMIT_SUCCESS) {
						dispatch(actions.fetchData());
					}
				});
		},
		affiliatesLookups: text => dispatch => {
			const request = http({
				url: `${endpoints.LOOKUPS.AFFILIATES}?query=${text}`,
				method: 'get',
				actions: [FETCH_SUGGEST_SUCCESS, FETCH_SUGGEST_ERROR]
			});

			return request(dispatch).then(action => {
				if (action.type === FETCH_SUGGEST_SUCCESS) {
					return {
						options: createSelectOptionsFromSysEnums(action.payload.data)
					};
				}
			});
		},
		markRisked: (id) => (dispatch, getState) => {
			return http({
				url: `${endpoints.PAYOUT_REQUESTS.BASE}/${id}/risky`,
				method: 'put',
				actions: [REQUEST_SUCCESS, REQUEST_ERROR]
			})(dispatch)
				.then(action => {
					if (action.type === REQUEST_SUCCESS) {
						dispatch(actions.fetchData());
					}
				});
		},
		offersLookup: text => (dispatch, getState) => {

			// const formData = selectors.getCreatePayoutRequestForm(getState());

			const query = qs.stringify({
				// affiliate_id: _get(formData, 'affiliate_id.0.value'),
				onlyAvailable: true,
				query: text
			});

			const request = http({
				url: `${endpoints.PAYOUT_REQUESTS.LOOKUP_OFFERS}?${query}`,
				method: 'get',
				actions: [FETCH_SUGGEST_SUCCESS, FETCH_SUGGEST_ERROR]
			});

			return request(dispatch).then(action => {
				if (action.type === FETCH_SUGGEST_SUCCESS) {
					return {
						options: createSelectOptionsFromSysEnums(action.payload.data)
					};
				}
			});
		},
		sidLookup: text => (dispatch, getState) => {

			const formData = selectors.getCreatePayoutRequestForm(getState());

			const query = qs.stringify({
				affiliate_id: _get(formData, 'affiliate_id.0.value'),
				offer_id: _get(formData, 'offer_id.0.value'),
				campaign_id: _get(formData, 'campaign_id.0.value'),
				country_id: _get(formData, 'country_id.0.value'),
				sid_type: _get(formData, 'sid_type.0.value'),
				onlyAvailable: true,
				query: text
			});

			const request = http({
				url: `${endpoints.PAYOUT_REQUESTS.LOOKUP_SIDS}?${query}`,
				method: 'get',
				actions: [FETCH_SUGGEST_SUCCESS, FETCH_SUGGEST_ERROR]
			});

			return request(dispatch).then(action => {
				if (action.type === FETCH_SUGGEST_SUCCESS) {
					return {
						options: createSelectOptionsFromSysEnums(action.payload.data)
					};
				}
			});
		},
		locationsLookupEditForm: (editingRequest, text) => (dispatch, getState) => {

			const query = qs.stringify({
				offer_id: editingRequest.offer_id,
				affiliate_id: editingRequest.affiliate_id,
				limit: 1000, search: text
			});

			const request = http({
				url: `${endpoints.PAYOUT_REQUESTS.LOOKUP_LOCATIONS}?${query}`,
				method: 'get',
				actions: [FETCH_SUGGEST_SUCCESS, FETCH_SUGGEST_ERROR]
			});

			return request(dispatch).then(action => {
				if (action.type === FETCH_SUGGEST_SUCCESS) {
					return { options: action.payload.data.map(item => ({ value: item.id, label: item.text })) };
				}
			});
		},
		campaignsLookupEditForm: (editingRequest, text) => (dispatch, getState) => {

			const formData = getFormValues('editPayoutRequestForm')(getState());
			const country_id = _get(formData, 'country_id.0.value', null);

			if (!country_id) {
				return [];
			}
			const query = qs.stringify({
				offer_id: editingRequest.offer_id,
				affiliate_id: editingRequest.affiliate_id,
				country_id: country_id,
				limit: 1000, query: text
			});

			const request = http({
				url: `${endpoints.LOOKUPS.CAMPAIGNS}?${query}`,
				method: 'get',
				actions: [FETCH_SUGGEST_SUCCESS, FETCH_SUGGEST_ERROR]
			});

			return request(dispatch).then(action => {
				if (action.type === FETCH_SUGGEST_SUCCESS) {
					return { options: action.payload.data.map(item => ({ value: item.id, label: item.text })) };
				}
			});
		},
		sidLookupEditForm: (editingRequest, text) => (dispatch, getState) => {

			const formData = getFormValues('editPayoutRequestForm')(getState());
			const country_id = _get(formData, 'country_id.0.value', null);
			const sid_type = _get(formData, 'sid_type.0.value');
			const campaign_id = _get(formData, 'campaign_id.0.value');

			if (!country_id || !sid_type || !campaign_id) {
				return [];
			}
			const query = qs.stringify({
				offer_id: editingRequest.offer_id,
				affiliate_id: editingRequest.affiliate_id,
				country_id: country_id,
				sid_type: sid_type,
				campaign_id: campaign_id,
				limit: 1000, query: text
			});

			const request = http({
				url: `${endpoints.PAYOUT_REQUESTS.LOOKUP_SIDS}?${query}`,
				method: 'get',
				actions: [FETCH_SUGGEST_SUCCESS, FETCH_SUGGEST_ERROR]
			});

			return request(dispatch).then(action => {
				if (action.type === FETCH_SUGGEST_SUCCESS) {
					return {
						options: createSelectOptionsFromSysEnums(action.payload.data)
					};
				}
			});
		},
		locationsLookup: text => (dispatch, getState) => {

			const formData = selectors.getCreatePayoutRequestForm(getState());
			const offer_id = _get(formData, 'offer_id.0.value');
			const affiliate_id = _get(formData, 'affiliate_id.0.value');

			if (!offer_id || !affiliate_id) {
				return {
					options: []
				};
			}
			const query = qs.stringify({ offer_id, affiliate_id, limit: 1000, search: text });

			const request = http({
				url: `${endpoints.PAYOUT_REQUESTS.LOOKUP_LOCATIONS}?${query}`,
				method: 'get',
				actions: [FETCH_SUGGEST_SUCCESS, FETCH_SUGGEST_ERROR]
			});

			return request(dispatch).then(action => {
				if (action.type === FETCH_SUGGEST_SUCCESS) {
					return { options: action.payload.data.map(item => ({ value: item.id, label: item.text })) };
				}
			});
		},
		campaignsLookup: text => (dispatch, getState) => {

			const formData = selectors.getCreatePayoutRequestForm(getState());
			const offer_id = _get(formData, 'offer_id.0.value');
			const affiliate_id = _get(formData, 'affiliate_id.0.value');
			const country_id = _get(formData, 'country_id.0.value');

			if (!offer_id || !affiliate_id) {
				return {
					options: []
				};
			}

			const query = qs.stringify({ offer_id, affiliate_id, query: text, country_id });

			const request = http({
				url: `${endpoints.LOOKUPS.CAMPAIGNS}?${query}`,
				method: 'get',
				actions: [FETCH_SUGGEST_SUCCESS, FETCH_SUGGEST_ERROR]
			});

			return request(dispatch).then(action => {
				if (action.type === FETCH_SUGGEST_SUCCESS) {
					return { options: action.payload.data.map(item => ({ value: item.id, label: item.text })) };
				}
			});
		},
		requestReview: (id) => (dispatch, getState) => {
			return http({
				url: endpoints.PAYOUT_REQUESTS.RESUBMIT.replace('{id}', id),
				method: 'put',
				actions: [SUBMIT_SUCCESS, SUBMIT_ERROR]
			})(dispatch)
				.then(action => {
					if (action.type === SUBMIT_SUCCESS) {
						dispatch(actions.fetchData());
					}
				});
		},
		remove: (id) => (dispatch, getState) => {
			return http({
				url: endpoints.PAYOUT_REQUESTS.SINGLE.replace('{id}', id),
				method: 'delete',
				actions: [SUBMIT_SUCCESS, SUBMIT_ERROR]
			})(dispatch)
				.then(action => {
					if (action.type === SUBMIT_SUCCESS) {
						dispatch(actions.fetchData());
					}
				});
		},
		clearDirtyFieldsAtCreateRequest: (clearFiledName) => (dispatch) => {
			switch (clearFiledName) {
				case 'affiliate_id': dispatch(change('createPayoutRequestForm', 'offer_id', null));// eslint-disable-line
				case 'offer_id': dispatch(change('createPayoutRequestForm', 'country_id', null));// eslint-disable-line
				case 'country_id': dispatch(change('createPayoutRequestForm', 'campaign_id', null));// eslint-disable-line
				case 'campaign_id': dispatch(change('createPayoutRequestForm', 'sid_type', null));// eslint-disable-line
				case 'sid_type': dispatch(change('createPayoutRequestForm', 'sid', null));// eslint-disable-line
			}
		},
		approveRequest: (id) => (dispatch, getState) => {
			return http({
				url: endpoints.PAYOUT_REQUESTS.APPROVE.replace('{id}', id),
				method: 'put',
				actions: [SUBMIT_SUCCESS, SUBMIT_ERROR]
			})(dispatch)
				.then(action => {
					if (action.type === SUBMIT_SUCCESS) {
						dispatch(actions.fetchData());
					}
				});
		},
		rejectRequest: (id) => (dispatch, getState) => {
			return http({
				url: endpoints.PAYOUT_REQUESTS.REJECT.replace('{id}', id),
				method: 'put',
				actions: [SUBMIT_SUCCESS, SUBMIT_ERROR]
			})(dispatch)
				.then(action => {
					if (action.type === SUBMIT_SUCCESS) {
						dispatch(actions.fetchData());
					}
				});
		},
		acceptRequest: (id) => (dispatch, getState) => {
			return http({
				url: endpoints.PAYOUT_REQUESTS.ACCEPT.replace('{id}', id),
				method: 'put',
				actions: [SUBMIT_SUCCESS, SUBMIT_ERROR]
			})(dispatch)
				.then(action => {
					if (action.type === SUBMIT_SUCCESS) {
						dispatch(actions.fetchData());
					}
				});
		},
		toggleSort: (col) => dispatch => {
			if (!col) {
				return;
			}
			dispatch({ type: TOGGLE_SORT, payload: col });
			dispatch(actions.fetchData());
		},
		toggleProgressBar: () => ({
			type: TOGGLE_PROGRESS_BAR
		})
	},
	calendar.actions,
	filters.actions
);

const reducers = Object.assign(
	{
		[LOCATION_CHANGE]: (state, action) => {
			return initialState;
		},
		[FETCH_DATA_SUCCESS]: (state, action) => {

			return {
				...state,
				data: action.payload.data,
				showProgressBar: false,
				error: false
			};
		},
		[FETCH_MATRIX_SUCCESS]: (state, action) => {

			return {
				...state,
				payoutMatrix: action.payload.data
			};
		},
		[FETCH_CURRENT_PAYOYT_SUCCESS]: (state, action) => {
			return {
				...state,
				currentPayout: action.payload.data,
				showProgressBar: false,
				error: false
			};
		},
		[APPLY]: (state, action) => {
			return {
				...state,
				selectedFilters: {
					...action.payload
				}
			};
		},
		[FETCH_DATA_ERROR]: state => ({ ...state, error: true, showProgressBar: false }),
		[SHOW_PROGRESS_BAR]: state => ({...state, showProgressBar: true}),
		[TOGGLE_REQUEST_FORM]: state => ({ ...state, isRequestFormVisible: !state.isRequestFormVisible }),
		[TOGGLE_EDIT_FORM]: (state, action) => ({
			...state,
			isRequestEditFormVisible: !state.isRequestEditFormVisible,
			editingId: action.payload
		}),
		[FETCH_SUGGEST_ERROR]: state => ({ ...state, error: true, showProgressBar: false }),
		[TOGGLE_PROGRESS_BAR]: state => ({ ...state }),
		[APPEND_MAX_LEADS_TO_REQUEST]: (state, action) => ({
			...state,
			data: state.data && state.data.map(item =>
				action.meta === item.id
					? ({ ...item, maxDailyLeads: action.payload.data })
					: ({ ...item })
			)
		}),
		[FETCH_MAX_DAILY_LEADS]: (state, action) => ({ ...state, maxDailyLeads: action.payload.data || 0 }),
		[TOGGLE_SORT]: (state, action) => ({
			...state,
			table: {
				...state.table,
				sortByColumn: {
					...state.table.sortByColumn,
					index: action.payload,
					direction: action.payload === state.table.sortByColumn.index &&
						state.table.sortByColumn.direction === 'asc' ? 'desc' : 'asc'
				}
			}
		})
	},
	calendar.reducers,
	filters.reducers
);

// reducers
export default handleActions(reducers, cloneDeep(initialState));

// selectors
export const selectors = {
	getData: state => state.payoutRequests.data,
	getCalendar: state => calendar.selectors.getCalendar(state => state.payoutRequests.calendar, state => state)(state),
	getProgressBar: state => state.payoutRequests.showProgressBar,
	getCreatePayoutRequestForm: state => getFormValues('createPayoutRequestForm')(state),
	getTable: state => {
		const { table, data, payoutMatrix } = state.payoutRequests;

		const rows = (data || []).map(item => ({
			...item,
			created_at_date: moment(item.created_at).format('DD-MM-YYYY'),
			created_at_time: moment(item.created_at).format('HH:mm:ss')
		}));

		return {
			headers: Object.values(table.columns),
			payoutMatrix,
			rows: rows
		};
	},
	getIsRequestFormVisible: state => state.payoutRequests.isRequestFormVisible,
	getEditingId: state => state.payoutRequests.editingId,
	getEditingRequest: state => {
		return _find(state.payoutRequests.data || [], { id: state.payoutRequests.editingId });
	},
	getIsRequestEditFormVisible: state => state.payoutRequests.isRequestEditFormVisible,
	getIsLoading: state => document.isLoading,
	getCurrentPayout: state => state.payoutRequests.currentPayout,
	getSortByColumn: state => state.payoutRequests.table.sortByColumn,
	getMaxDailyLeads: state =>  state.payoutRequests.maxDailyLeads,
	getIntl: state => intl(state),
	getTrafficSources: state => createSelectOptionsFromSysEnums(state.root.lists['traffic-sources']),
	getCan: state => checkPermissions(state, allowedPermissions),
	getFilters: state => filters.selectors.getFilters(
		state => state.payoutRequests.filters,
		state => state
	)(state),
	getRequestFormInitialValues: state => ({
		currency: 'usd'
	}),
	getRequestEditFormInitialValues: state => ({
		currency: 'usd',
		is_traffic_scaling: _get(
			_find(selectors.getData(state), { id: selectors.getEditingId(state) }),
			'is_traffic_scaling',
			false
		)
	}),
	getError: state => state.payoutRequests.error
};



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/payout-requests/list-request/index.js